import { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import dynamic from "next/dynamic";
import { EnumFeedType } from "src/api/server/feed.types";
import type { IWikifolioFeedDict } from "src/api/server/wf-detail.types";
import { EnumPageType, initializeFeedTrackingStore } from "src/components/feed-card/store/feed-tracking-store";
import { useFeedItemSeenTracking } from "src/components/pages/feed/hooks/use-feed-item-seen-tracking";
import { useInfiniteFeed } from "src/components/pages/feed/hooks/use-infinite-feed";
import { WfHeading } from "src/components-mui/base/wf-heading/wf-heading";
import { ShowMoreButton } from "src/components-mui/common/buttons/show-more-button/show-more-button";
import { useIsSectionInView } from "src/components-mui/common/jump-marks/use-is-section-in-view";
import { API_FEED_V3_WIKIFOLIO } from "src/constants/urls";
import { useUserStore } from "src/stores/user-store";
import { ContentSeenItem } from "../../feed/types/types";
import { FeedLoadingState } from "./components/feed-loading-state";
import { FeedStateRenderer } from "./components/feed-state-renderer";
import { usePinnedCommentId } from "./hooks/use-pinned-comment-id";
import { getPinnedAndUnpinnedFeed } from "./utils/get-pinned-and-visible-feed";
import { getShouldShowMoreButton } from "./utils/get-should-show-more-button";

export interface IFeedSectionProps {
    wikifolioId: string;
    traderId: string;
    dict: IWikifolioFeedDict;
}

const LazyFeedContent = dynamic(() => import("./components/feed-content").then(m => m.FeedContent), {
    loading: () => <FeedLoadingState />,
});

export const FeedSection = ({ wikifolioId, traderId, dict }: IFeedSectionProps) => {
    const [isShowMoreClicked, setIsShowMoreClicked] = useState(false);

    const { user } = useUserStore();
    const isOwner = user?.id === traderId;

    const sectionRef = useRef<HTMLDivElement>(null);
    const isVisible = useIsSectionInView({ sectionRef, threshold: 0.1, visibleOnce: true });

    const { feed, isLoading, isError, hasNextPage, fetchNextPage, refetch } = useInfiniteFeed({
        feedUrl: API_FEED_V3_WIKIFOLIO,
        feedType: EnumFeedType.WikifolioEntity,
        wikifolioId,
        enabled: isVisible,
    });
    const feedItems = feed ?? null;

    const seenItemsRef = useRef<Map<string, ContentSeenItem>>(new Map());
    const { trackSeenItem } = useFeedItemSeenTracking(seenItemsRef);

    useEffect(() => {
        initializeFeedTrackingStore({
            page: EnumPageType.WikifolioDetail,
            wikifolioId,
        });
    }, [wikifolioId]);

    const handleShowMore = useCallback(() => {
        if (isShowMoreClicked) {
            void fetchNextPage();

            return;
        }

        setIsShowMoreClicked(true);
    }, [isShowMoreClicked, fetchNextPage]);

    const pinnedCommentId = usePinnedCommentId({ wikifolioId, feed: feedItems });
    const { pinnedCard, unpinnedFeed } = getPinnedAndUnpinnedFeed(feedItems, pinnedCommentId);
    const shouldShowMoreButton = getShouldShowMoreButton(hasNextPage, isShowMoreClicked, unpinnedFeed, Boolean(pinnedCard));

    return (
        <Box component="section" ref={sectionRef}>
            <WfHeading component="h2" fontSize="2xl" sx={{ mb: 2 }}>
                {dict.title}
            </WfHeading>

            <FeedStateRenderer feed={feedItems} isLoading={isLoading} isError={isError} dict={dict}>
                <LazyFeedContent
                    feed={feedItems}
                    isShowMoreClicked={isShowMoreClicked}
                    isOwner={isOwner}
                    onFirstSeen={trackSeenItem}
                    wikifolioId={wikifolioId}
                    refetchFeed={refetch}
                    reportModalPlaceholder={dict.commentingModalEditorPlaceholder}
                />

                <ShowMoreButton isVisible={shouldShowMoreButton} onClick={handleShowMore} />
            </FeedStateRenderer>
        </Box>
    );
};
